import React, { useState } from 'react';
import { CLOSE_REASONS, VISIT_HAPPENED_STATUSES } from './LiveJobsBoard';

// One dialog for every close from Live Jobs. It says what will happen and asks why, and the
// answer travels as the close reason. Before this, closing before the door cancelled with no
// confirmation at all, and closing after it force-completed behind a different message.
export default function CloseStopDialog({ visit, visits, busy, onCancel, onConfirm }) {
  const [reasonKey, setReasonKey] = useState(CLOSE_REASONS[0].key);
  const name = visit.customer_name || visit.source_job_id;
  const tripStops = visit.trip_job_ids
    ? visits
        .filter(other => visit.trip_job_ids.includes(other.source_job_id))
        .sort((a, b) => (a.stop_sequence || 0) - (b.stop_sequence || 0))
    : [];
  const next = tripStops.find(
    other => !other.finished && other.source_job_id !== visit.source_job_id
      && (other.stop_sequence || 0) > (visit.stop_sequence || 0)
  );
  const boltOns = visits.filter(
    other => other.bolt_on_of === visit.source_job_id && !other.finished
  );
  const reason = CLOSE_REASONS.find(item => item.key === reasonKey);
  const made = VISIT_HAPPENED_STATUSES.has(visit.status);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/45 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-[540px] rounded-lg bg-white px-6 py-5 shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-gray-900">Close {name}&rsquo;s stop?</h3>
        <div className="mt-1 text-[13px] text-gray-500">
          {visit.stop_sequence
            ? `Stop ${visit.stop_sequence} of ${visit.trip_job_ids.length} on ${visit.rider_name || 'the rider'}’s trip · `
            : `${visit.rider_name || 'The rider'} · `}
          {(visit.status || '').replace(/_/g, ' ').toLowerCase()}
        </div>

        <div className="mt-4 space-y-2 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-3 text-sm leading-5 text-gray-700">
          <div>
            {next ? (
              <>
                {visit.rider_name || 'The rider'} gets a notification now and goes on to{' '}
                <b>{next.customer_name || next.source_job_id}</b> (stop {next.stop_sequence}).
              </>
            ) : visit.trip_job_ids ? (
              <>{visit.rider_name || 'The rider'} gets a notification now and heads back with the bags.</>
            ) : (
              <>{visit.rider_name || 'The rider'} gets a notification now and is free once their bag is handed over.</>
            )}
          </div>
          <div>
            {made
              ? 'The bag stays with the rider and comes back at the end of the trip. Reverse it as usual.'
              : 'If the pack is already collected it comes back with the rider; otherwise it stays at the warehouse. Reverse it as usual.'}
          </div>
          {boltOns.length > 0 && (
            <div>
              {boltOns.map(b => b.customer_name || b.source_job_id).join(', ')} rides on this
              bag, so the rider hands the unsold pieces on to them.
              {reason.returns ? '' : ' Make sure the sale is on the Orders sheet first.'}
            </div>
          )}
        </div>

        <div className="mt-4 mb-2 text-sm font-semibold text-gray-800">Why is the stop being closed?</div>
        {CLOSE_REASONS.map(item => (
          <label
            key={item.key}
            className={`mb-2 flex cursor-pointer gap-2.5 rounded-md border px-3 py-2.5 ${
              reasonKey === item.key ? 'border-gray-800 bg-gray-50' : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name="close-reason"
              checked={reasonKey === item.key}
              onChange={() => setReasonKey(item.key)}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-800">{item.label}</span>
              <span className="block text-[12.5px] leading-[18px] text-gray-500">{item.detail}</span>
            </span>
          </label>
        ))}

        {visit.live_bill_amount && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] leading-[19px] text-amber-800">
            The unpaid rider-app bill of ₹{visit.live_bill_amount} will be cancelled, so it
            can&rsquo;t be paid twice.
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-gray-300 px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Keep the stop
          </button>
          <button
            onClick={() => onConfirm(visit, reason.text, reason.returns ? (made ? 'RTO' : 'DOL') : undefined)}
            disabled={busy}
            className="rounded bg-red-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
          >
            {busy ? 'Closing...' : `Close ${name}’s stop`}
          </button>
        </div>
      </div>
    </div>
  );
}
